'use client'

import { parseColor, UseColorPickerContext } from '@ark-ui/react'
import { ColorPicker as ColorPickerPrimitive } from '@ark-ui/react/color-picker'
import { useResizeObserver } from '@mantine/hooks'
import { Portal } from '@radix-ui/react-portal'
import { ChangeEventHandler, ComponentPropsWithRef, useRef } from 'react'

import { cn, getVersionForClone, isColorWhite, safeParseColor } from '@/utils'

import { Input } from '../Input'
import {
	ColorPickerFormatProvider,
	ColorPickerValueProvider,
	TColorInputFormat,
} from './ChannelInputs'
import { ColorPickerContent, TColorPickerMode } from './ColorPickerContent'

const getHexInputChangeHandler =
	(api: UseColorPickerContext): ChangeEventHandler<HTMLInputElement> =>
	(event) => {
		let { value } = event.target
		value = value.replace(/[^0-9abcdefABCDEF]/g, '').toUpperCase()
		if (!value.startsWith('#')) value = `#${value}`
		value = value.substring(0, 7)
		event.target.value = value
		api.getChannelInputProps({ channel: 'hex' }).onChange?.(event)
	}

const ColorPicker = ({
	value,
	setValue,
	onValueChange,
	label,
	initialMode,
	transparentLabel,
	translations,
	...props
}: Omit<
	ColorPickerPrimitive.RootProps,
	'value' | 'setValue' | 'onValueChange'
> & {
	value: string
	setValue: (hex: string) => void
	onValueChange: (hex: string) => void
	label: string
	initialMode?: TColorPickerMode
	transparentLabel?: string
	translations: {
		switchSolidModeLabel: string
		switchPaletteModeLabel: string
	}
}) => {
	const parsedValue = safeParseColor(parseColor)(value)
	const normalizedValue = parsedValue ? value : ''
	const [containerRef, { width: containerWidth }] = useResizeObserver()
	const controlRef = useRef<HTMLDivElement>(null)
	const isValueWhite = isColorWhite(parseColor)(value)

	return (
		<ColorPickerPrimitive.Root
			ref={containerRef}
			{...props}
			value={parsedValue ?? undefined}
			format="hsla"
			className={cn('w-full', props.className)}
			positioning={{
				gutter: 20,
				placement: 'bottom-end',
				offset: { crossAxis: 16 },
			}}
			onValueChange={({ valueAsString }) => {
				onValueChange(valueAsString)
			}}
			openAutoFocus={false}
		>
			<ColorPickerPrimitive.Context>
				{(api) => {
					const inputProps = api.getChannelInputProps({ channel: 'hex' })
					const input =
						!normalizedValue && transparentLabel ? (
							<Input
								// Ensures React renders a different input when the value is transparent to prevent the same component switching between controlled and uncontrolled
								key="transparent-input"
								type="text"
								defaultValue={transparentLabel}
								disabled={props.disabled}
								className="pointer-events-none"
							/>
						) : (
							<Input
								key="regular-input"
								{...inputProps}
								onChange={getHexInputChangeHandler(api)}
								disabled={props.disabled}
								className="w-full pr-14"
							/>
						)

					return (
						<>
							<ColorPickerPrimitive.Label className="type-body-medium font-semibold">
								{label}
							</ColorPickerPrimitive.Label>
							<ColorPickerPrimitive.Control
								ref={controlRef}
								className="relative mt-2"
							>
								{input}
								<ColorPickerPrimitive.Trigger asChild>
									<button
										className={cn(
											'!absolute right-4 top-1/2 size-6 -translate-y-1/2 border border-transparent transition',
											getVersionForClone({
												base: 'rounded-8',
												clone1: 'rounded-full',
											}),
											isValueWhite && 'border-border-grey',
											props.disabled && 'cursor-not-allowed opacity-0',
										)}
										style={{ backgroundColor: value }}
									/>
								</ColorPickerPrimitive.Trigger>
							</ColorPickerPrimitive.Control>
							<Portal>
								<ColorPickerPrimitive.Positioner>
									<ColorPickerValueProvider value={{ value, setValue }}>
										<ColorPickerContent
											setValue={(color) => {
												const hex = color.toString('hex')
												setValue(hex)
											}}
											containerWidth={containerWidth}
											initialMode={initialMode}
											translations={translations}
										/>
									</ColorPickerValueProvider>
								</ColorPickerPrimitive.Positioner>
							</Portal>
						</>
					)
				}}
			</ColorPickerPrimitive.Context>
			<ColorPickerPrimitive.HiddenInput />
		</ColorPickerPrimitive.Root>
	)
}

const ColorPickerWithFormatProvider = ({
	initialSolidModeInputFormat = 'rgb',
	...props
}: ComponentPropsWithRef<typeof ColorPicker> & {
	initialSolidModeInputFormat?: TColorInputFormat
}) => (
	<ColorPickerFormatProvider initialValue={initialSolidModeInputFormat}>
		<ColorPicker {...props} />
	</ColorPickerFormatProvider>
)

export { ColorPickerWithFormatProvider as ColorPicker }
